import { Facets } from 'utility/types/dataProvider';
import qs from 'qs';
import { httpClient, qsOptions } from 'providers/clients/httpClient';

// URL Map Item - core.urlmap.instance
export type UrlMapItem = {
  /** URL map's identifier (uuid) */
  id?: string;
  /** URL map name */
  name?: string;
  /** The URL's label */
  label?: string | null;
  /** Whether or not this URL map is active */
  active?: boolean;
  /** The map's global skin name */
  global?: string;
  /** The map's template library name */
  template_library?: string;
};

// URL Map Node Instance - core.urlmap.url.instance
export type UrlMapNodeInstance = {
  /** Unique identifier for the URL */
  id?: number;
  /** ID of the URL's immediate parent */
  parent_id?: number;
  /** The URL's immediate node name */
  name?: string;
  /** The URL's full path, relative to site root */
  path?: string;
  /** The URL's alias, if any */
  alias?: string;
  /** Whether the URL is a leaf node */
  leaf?: boolean;
  /** The URL's application */
  app?: string;
  /** The URL's template skin */
  template?: string;
  /** Whether the URL should inherit its parent's app and template */
  template_inherit?: boolean;
  /** The URL's title */
  title?: string;
  /** The URL's label */
  label?: string;
  /** Whether the URL is allowed to be archived */
  allow_archiving?: boolean;
  /** Whether the URL should be prevented from being indexed by search engines */
  prevent_indexing?: boolean;
  /** Whether the URL should be included in site search results */
  searchable?: boolean;
  /** Customizations defined directly on the URL node as name/value pairs */
  customizations?: UrlMapCustomizations;
  /** The URL node's customizations merged with customizations inherited from its parent node */
  inherited_customizations?: UrlMapCustomizations;
};

type GetUrlMapArgs = {
  limit: number;
  page?: string | number;
};

export type GetUrlMapResponse = {
  status: string | number;
  data: {
    items: UrlMapItem[];
    total: number;
    next?: string | number | null;
    prev?: string | number | null;
    facets?: Facets;
  };
};

type GetUrlMapRootNodeArgs = {
  map_id: string;
};

/**
 * Common generic for the endpoints that
 * return a single UrlMapNodeInstance
 */
type GenericUrlMapInstanceResponse = {
  status: string | number;
  data: UrlMapNodeInstance;
};

type GetUrlMapRootNodeResponse = GenericUrlMapInstanceResponse;

/**
 * note: url_id params arguments
 * numbers are converted to strings in url paths
 */
type GetUrlMapNodeArgs = {
  map_id: string;
  url_id: number | string;
};

type GetUrlMapNodeResponse = GenericUrlMapInstanceResponse;

type CreateUrlMapNodeChildArgs = {
  map_id: string;
  url_id: number | string;
  body: UrlMapNodeInstance;
};

type CreateUrlMapNodeChildResponse = GenericUrlMapInstanceResponse;

export type UpdateUrlMapNodeArgs = {
  map_id: string;
  url_id: number | string;
  body: UrlMapNodeInstance;
};

export type UpdateUrlMapNodeResponse = GenericUrlMapInstanceResponse;

type DeleteUrlMapNodeArgs = {
  map_id: string;
  url_id: number | string;
};

// status only response (eg 204 no content)
type DeleteUrlMapNodeResponse = {
  status?: string | number;
  [key: string]: any;
};

type GetUrlMapNodeChildrenArgs = {
  map_id: string;
  url_id: number | string;
};

type GetUrlMapNodeChildrenResponse = {
  status: string | number;
  data: UrlMapNodeInstance[];
};

// URL Map Depth Summary
// core.urlmap.url.depth_summary
export type UrlMapDepthSummary = {
  id?: number;
  parent_id?: number;
  name?: string;
  path?: string;
  alias?: string | null;
  leaf?: boolean;
  app?: string;
  template?: string | null;
  template_inherit?: boolean;
  /** The URL's immediate children */
  children?: UrlMapDepthSummary[];
};

type SearchUrlMapArgs = {
  map_id: string;
  query?: string;
  app?: string;
  alias?: string;
};

export type SearchUrlMapResponse = {
  status: string | number;
  data: UrlMapDepthSummary[];
};

export const urlMapProvider = {
  // Get site URL maps
  getUrlMap: async ({ limit, page }: GetUrlMapArgs): Promise<GetUrlMapResponse> => {
    try {
      // force minimum limit of 1
      if (!limit || limit <= 0) {
        limit = 1;
      }

      const basePath = `/core/urlmap/`;

      const payload = {
        ...(limit && { limit: limit }),
        ...(page && { page: page }),
      };

      // prepend qps if at least one key in payload
      const qpStr = Object.keys(payload).length
        ? `?${qs.stringify(payload, qsOptions)}`
        : '';

      const finalPath = `${basePath}${qpStr}`;

      const { data, status } = await httpClient({
        url: finalPath,
      });

      const result: GetUrlMapResponse = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getUrlMap] Error:', error);
      throw error;
    }
  },

  // Get a URL map's root ('/') node
  getUrlMapRootNode: async ({
    map_id,
  }: GetUrlMapRootNodeArgs): Promise<GetUrlMapRootNodeResponse> => {
    try {
      const path = `/core/urlmap/${map_id}/root/`;

      const { data, status } = await httpClient({
        url: path,
      });

      const result: GetUrlMapRootNodeResponse = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getUrlMapRootNode] Error:', error);
      throw error;
    }
  },

  // Get URL map node
  getUrlMapNode: async ({
    map_id,
    url_id,
  }: GetUrlMapNodeArgs): Promise<GetUrlMapNodeResponse> => {
    try {
      const path = `/core/urlmap/${map_id}/nodes/${url_id}/`;

      const { data, status } = await httpClient({
        url: path,
      });

      const result: GetUrlMapNodeResponse = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getUrlMapNode] Error:', error);
      throw error;
    }
  },

  // Create a new URL map node child
  createUrlMapNodeChild: async ({
    map_id,
    url_id,
    body,
  }: CreateUrlMapNodeChildArgs): Promise<CreateUrlMapNodeChildResponse> => {
    try {
      const path = `/core/urlmap/${map_id}/nodes/${url_id}/children/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(body),
      });

      const result: CreateUrlMapNodeChildResponse = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[createUrlMapNodeChild] Error:', error);
      throw error;
    }
  },

  // Update a URL map node
  updateUrlMapNode: async ({
    map_id,
    url_id,
    body,
  }: UpdateUrlMapNodeArgs): Promise<UpdateUrlMapNodeResponse> => {
    try {
      const path = `/core/urlmap/${map_id}/nodes/${url_id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(body),
      });

      const result: UpdateUrlMapNodeResponse = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[updateUrlMapNode] Error:', error);
      throw error;
    }
  },

  // Delete a URL map node
  deleteUrlMapNode: async ({
    map_id,
    url_id,
  }: DeleteUrlMapNodeArgs): Promise<DeleteUrlMapNodeResponse> => {
    try {
      const path = `/core/urlmap/${map_id}/nodes/${url_id}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });

      const result: DeleteUrlMapNodeResponse = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[deleteUrlMapNode] Error:', error);
      throw error;
    }
  },

  // Get URL map node children
  getUrlMapNodeChildren: async ({
    map_id,
    url_id,
  }: GetUrlMapNodeChildrenArgs): Promise<GetUrlMapNodeChildrenResponse> => {
    try {
      const path = `/core/urlmap/${map_id}/nodes/${url_id}/children/`;

      const { data, status } = await httpClient({
        url: path,
      });

      const result: GetUrlMapNodeChildrenResponse = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getUrlMapNodeChildren] Error:', error);
      throw error;
    }
  },

  // Perform a depth search of a URL map
  searchUrlMap: async ({
    map_id,
    query,
    app,
    alias,
  }: SearchUrlMapArgs): Promise<SearchUrlMapResponse> => {
    if (!map_id) {
      throw new Error('Missing required parameter: map_id');
    }

    try {
      const path = `/core/urlmap/${map_id}/depth/`;

      const params = new URLSearchParams({
        ...(alias && { alias }),
        ...(query && { query }),
        ...(app && { app }),
      });

      const stringPath = `${path}?${params.toString()}`;

      const { data, status } = await httpClient({
        url: stringPath,
      });

      const result: SearchUrlMapResponse = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[searchUrlMap] Error:', error);
      throw error;
    }
  },
  // as const - all keys (functions) in this object are readonly
} as const;

// Since all functions have their own signature with types
// we can safely use typeof helper to create the type for us
// and then '&' add it to the BloxCustomDataProvider type
export type UrlMapProviderType = typeof urlMapProvider;

type UrlMapFile = {
  url?: string; // generally an image url
  id?: string;
  width?: number;
  height?: number;
  size?: number;
  [key: string]: any;
};

/**
 * Likely non-exhaustive list of customization keys
 * adding what was seen in a few real fetch examples.
 */
export type UrlMapCustomizations = {
  grid?: string;
  theme?: string;
  sidebar?: string;
  top_bar?: string;
  bg_color?: string;
  body_color?: string;
  article_navigation?: string;
  article_layout?: string;
  article_aside?: string;
  article_top_width?: string;
  letterbox_style?: string;
  nav_style?: string;
  nav_position?: string;
  nav_event_style?: string;
  link_color?: string;
  accent_color?: string;
  main_nav_color?: string;
  footer_bg?: string;
  footer_nav_color?: string;
  footer_text_color?: string;
  content_text_color?: string;
  top_bar_bg?: string;
  top_nav_color?: string;
  nav_bg?: string;
  body_text_color?: string;
  og_image?: UrlMapFile;
  google_amp_logo?: UrlMapFile;
  apple_touch_icon?: UrlMapFile;
  google_amp_footer_logo?: UrlMapFile;
  google_amp_publisher_logo?: UrlMapFile;
  ads_dfp_domain?: string;
  ads_dfp_network?: string;
  ads_fixed_smart?: string;
  ads_dfp_lazyload?: string;
  ads_video_vast_url?: string;
  ads_gallery_refresh?: number;
  ads_interval_refresh?: string;
  ads_placement_infinity?: string;
  ads_fixed_tier_provider?: string;
  ads_fixed_tier?: string;
  ads_blocker?: string;
  ads_hide_audience?: string;
  ads_index_slug?: string;
  ads_infinity_asset?: string;
  ads_infinity_space?: string;
  ads_overline?: string;
  ads_multimedia_gallery_insert?: string;
  bidding_a9_publisher_id?: string;
  bidding_prebid?: string;
  bidding_refresh?: string;
  bidding_ozone_script_url?: string;
  bidding_magnite_script_url?: string;
  google_amp?: string;
  google_amp_terms?: string;
  google_amp_privacy?: string;
  google_amp_outbrain?: string;
  google_amp_sticky_ad?: string;
  google_amp_a9_publisher_id?: string;
  google_amp_flying_carpet_ad?: string;
  google_amp_ads_placement_infinity?: string;
  google_amp_ad_unit?: string;
  google_amp_vast_url?: string;
  google_amp_gtm_id?: string;
  google_amp_navigation_sidebar?: string;
  comscore_c2?: string;
  chartbeat_uid?: string;
  google_maps_api_key?: string;
  google_site_verification?: string;
  google_verify?: string;
  parsely_id?: string;
  facebook_verify?: string;
  bitly_verification?: string;
  yahoo_verify?: string;
  nielsen_apid?: string;
  quantcast_id?: string;
  infinity_scroll?: string;
  infinity_scroll_order?: string;
  infinity_story_siderail_widget?: string;
  infinity_ad?: string;
  twitter_site?: string;
  bluesky?: string;
  linked_in?: string;
  fb_publisher_url?: string;
  fb_app_id?: string;
  fbia_claim_id?: string;
  fbia_ad_network?: string;
  fbia_dfp_prefix?: string;
  fbia_fb_placement_id?: string;
  show_asset_icons?: string;
  share_button_type?: string;
  fixed_share?: string;
  text_to_speech?: string;
  user_reactions?: string;
  user_reactions_text?: string;
  user_reactions_explanation_text?: string;
  star_ratings?: string;
  star_ratings_text?: string;
  star_ratings_explanation_text?: string;
  metering_mode?: string;
  metering_count?: string;
  metering_total_free?: string;
  metering_messages?: string;
  metering_preview_paragraphs?: string;
  metering_prompt_login_after?: string;
  metering_allowed_referrers?: string;
  metering_allowed_referrers_enable?: string;
  metering_archive_after_days?: string;
  metering_services_on_wall?: string;
  metering_paywall_action?: string;
  paywall_theme?: string;
  paywall_display?: string;
  paywall_login_prompt?: string;
  paywall_rate_display?: string;
  show_premium?: string;
  comment_mode?: string;
  comment_enable?: string;
  comment_rules?: string;
  comment_livefyre_id?: string;
  comment_worldtable_id?: string;
  comment_disqus_shortname?: string;
  comment_availability?: string;
  comments_new_page?: string;
  gallery_engagement?: string;
  time_to_read?: string;
  video_auto_play?: string;
  video_auto_advance?: string;
  article_breakthrough?: string;
  asset_author_display?: string;
  related_asset_display?: string;
  video_media_slideshow?: string;
  search_url?: string;
  search_section?: string;
  site_component?: string;
  section_count?: string;
  outbrain?: string;
  insticator?: string;
  dmp_provider?: string;
  evvnt_api_id?: string;
  evvnt_publisher_name?: string;
  lotame_click?: string;
  lotame_impression?: string;
  apple_app_id?: string;
  android_app_id?: string;
  here_maps_app_id?: string;
  here_maps_app_code?: string;
  here_maps_api_key?: string;
  units?: string;
  language?: string;
  weather_city?: string;
  weather_zip?: string;
  community_url?: string;
  ms_validate?: string;
  css_instance?: string;
  aspect_ratio?: string;
  mast_head?: string;
  masthead_bg?: string;
  mobile_nav_style?: string;
  mobile_nav_inherited?: string;
  is_fluid_footer?: string;
  is_fluid_header?: string;
  page_width?: string;
  // any other key/value pairs
  [key: string]: any;
};
